
import { call, put, select, takeEvery } from 'redux-saga/effects'
import GeoJSON from 'ol/format/GeoJSON';
import { fromExtent as polygonFromExtent } from 'ol/geom/Polygon';
import {
    extend as extentExtend,
    getIntersection as extentGetIntersection
} from 'ol/extent';

import { createActionTypes } from '../../common/client/sagas/common';
import { ERROR_CLEAR_ACTION_TYPE, ERROR_CREATE_ACTION_TYPE } from '../../common/client/sagas/operationStatus';
import server from '../server';

import { dispatchEventWithGeneric } from '../../core';
import { sagas as sagasMap } from '../../map'

import createEventSearchResult from '../events/searchResult';

export const DO_SEARCH_ACTION_TYPE = createActionTypes('search/DO_SEARCH');

export const SET_INITIALIZED = 'search/SET_INITIALIZED';
export const CLEAR_SEARCH_RESULT = 'search/CLEAR_SEARCH_RESULT';

import { Log } from '../../core';
const log = Log.getLogger('MultiSceneForApp.search.sagas.search');

export default function getActions(dispatch) {
    return {
        clearSearchResult: () => dispatch({
            type: CLEAR_SEARCH_RESULT
        }),
        doSearch: (projectId, queryId, searchData, clientOptions, debug, operation) => dispatch({
            type: DO_SEARCH_ACTION_TYPE.DEFAULT,
            projectId,
            queryId,
            searchData,
            clientOptions,
            debug,
            operation
        }),
        setInitialized: (value) => dispatch({
            type: SET_INITIALIZED,
            value
        })
    };
}

function doSearch(action) {
    return new Promise((resolve, reject) => {
        server.search.doSearch(
            action.projectId, action.queryId, action.searchData, action.clientOptions, action.debug,
            (err, searchResult) => {
                if (searchResult) {
                    resolve(searchResult);
                } else {
                    reject(err);
                }
            }
        );
    });
}

function processSearchResult(previousSearchResult, searchResult, operation) {
    operation = operation || 'SET';
    switch (operation) {
        case 'ADD':
            return processSearchResultAdd(previousSearchResult, searchResult);
        case 'FILTER':
            return processSearchResultFilter(previousSearchResult, searchResult);
        case 'REMOVE':
            return processSearchResultRemove(previousSearchResult, searchResult);
        case 'SET':
            return processSearchResultSet(previousSearchResult, searchResult);
        default:
            log.warn(`unknown search operation "${operation}"`);
            break;
    }
}

function processSearchResultAdd(previousSearchResult, searchResult) {
    let previousObjects = (previousSearchResult ? previousSearchResult.objects : []);
    let existingIds = previousObjects.map(item => item.id);

    let objects = [
        ...previousObjects,
        ...(searchResult.objects.filter(item => (existingIds.indexOf(item.id) === -1)))
    ];

    let format = new GeoJSON();
    let extent;

    if (previousSearchResult && previousSearchResult.extent) {
        let geometry1 = format.readGeometry(previousSearchResult.extent);
        if (searchResult.extent) {
            let geometry2 = format.readGeometry(searchResult.extent);
            extent = extentExtend(geometry1.getExtent(), geometry2.getExtent());
        } else {
            extent = geometry1.getExtent();
        }
    } else {
        if (searchResult.extent) {
            extent = format.readGeometry(searchResult.extent).getExtent();
        }
    }

    return {
        clientDesc: searchResult.clientDesc,
        count: objects.length,
        extent: extent ? format.writeGeometry(polygonFromExtent(extent)) : null,
        info: searchResult.info || (previousSearchResult ? previousSearchResult.info : null),
        objects: objects,
        queryId: searchResult.queryId
    };
}

function processSearchResultFilter(previousSearchResult, searchResult) {
    let previousObjects = (previousSearchResult ? previousSearchResult.objects : []);
    let existingIds = previousObjects.map(item => item.id);

    let objects = [
        ...(searchResult.objects.filter(item => (existingIds.indexOf(item.id) !== -1)))
    ];

    let extent;
    let format = new GeoJSON();
    if (previousSearchResult && previousSearchResult.extent) {
        let geometry1 = format.readGeometry(previousSearchResult.extent);
        if (searchResult.extent) {
            let geometry2 = format.readGeometry(searchResult.extent);
            extent = extentGetIntersection(geometry1.getExtent(), geometry2.getExtent());
        } else {
            extent = geometry1.getExtent();
        }
    }

    return {
        clientDesc: searchResult.clientDesc,
        count: objects.length,
        extent: extent ? format.writeGeometry(polygonFromExtent(extent)) : null,
        info: searchResult.info || (previousSearchResult ? previousSearchResult.info : null),
        objects: objects,
        queryId: searchResult.queryId
    };
}

function processSearchResultRemove(previousSearchResult, searchResult) {
    let previousObjects = (previousSearchResult ? previousSearchResult.objects : []);
    let resultIds = searchResult.objects.map(item => item.id);

    let objects = [
        ...(previousObjects.filter(item => (resultIds.indexOf(item.id) === -1)))
    ];

    let extent;
    let format = new GeoJSON();
    if (previousSearchResult && previousSearchResult.extent) {
        let geometry1 = format.readGeometry(previousSearchResult.extent);
        let geometry2 = format.readGeometry(searchResult.extent);
        // ToDo: returned extent must be result of operation(g1.extent NOT g2.extent)
        extent = geometry1.getExtent();
    }

    return {
        clientDesc: searchResult.clientDesc,
        count: objects.length,
        extent: extent ? format.writeGeometry(polygonFromExtent(extent)) : null,
        info: searchResult.info || (previousSearchResult ? previousSearchResult.info : null),
        objects: objects,
        queryId: searchResult.queryId
    };
}

function processSearchResultSet(previousSearchResult, searchResult) {
    return searchResult;
}

function* workDoSearch(action) {
    try {
        let previousSearchResult = yield select((state) => state.search.search.searchResult);

        let silent = action.clientOptions ? (action.clientOptions.silent === true) : false;

        yield put({type: ERROR_CLEAR_ACTION_TYPE});
        if (!silent) {
            yield put({type: DO_SEARCH_ACTION_TYPE.LOADING});
        }
        const searchResult = yield call(doSearch, action);

        yield call(
            dispatchEventWithGeneric,
            createEventSearchResult(
                searchResult
            )
        );

        if (!silent) {
            const processedSearchResult = processSearchResult(
                previousSearchResult, searchResult, action.operation
            );

            yield put({type: DO_SEARCH_ACTION_TYPE.SUCCESS, searchResult: processedSearchResult});
            yield put({
                type: sagasMap.ADD_COMMAND,
                name: 'SEARCH_ADD_PINS',
                params: {
                    items: processedSearchResult.objects.map(item => {
                        return {
                            ...item,
                            _info: processedSearchResult.info,
                            _queryId: processedSearchResult.queryId
                        };
                    })
                }
            });

            if (processedSearchResult.count > 0) {
                yield put({
                    type: sagasMap.ADD_COMMAND,
                    name: 'ZOOM_FEATURE',
                    params: {geoJson: processedSearchResult.extent
                }});
            }
        } else {
            yield put({type: DO_SEARCH_ACTION_TYPE.SUCCESS, searchResult: null});
        }

    } catch (error) {
        log.error(error);
        yield put({type: DO_SEARCH_ACTION_TYPE.FAILED, error});
        yield put({type: ERROR_CREATE_ACTION_TYPE, error});
    }
}

export function* watchDoSearch() {
    yield takeEvery(DO_SEARCH_ACTION_TYPE.DEFAULT, workDoSearch);
}

function* workClearSearchResult(action) {
    yield put({
        type: sagasMap.ADD_COMMAND,
        name: 'SEARCH_ADD_PINS',
        params: {items: []}
    });
}

export function* watchClearSearchResult() {
    yield takeEvery(CLEAR_SEARCH_RESULT, workClearSearchResult);
}
